import {
  Input,
  signal,
  computed,
  inject,
  OnInit,
  Component,
  viewChild,
  OnDestroy,
  forwardRef,
  ElementRef,
  DestroyRef,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Store } from '@ngrx/store';
import { NgpPopoverTrigger } from 'ng-primitives/popover';
import {
  faTimes,
  faXmark,
  faFloppyDisk,
} from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';

import { resolveSuiBoundary } from '@sui/shared';

import Pickr from './vendor/js/pickr';

import { AppState } from 'src/app/reducers';
import { selectUserCompanyColorScheme } from 'src/app/core/reducers';
import { DEFAULT_PPRESET_COLORS } from 'src/app/core/constants';

import {
  hexToRgb,
  rgbaToNormalizedHex,
} from 'src/app/shared/utils/color-utils';

import { tap } from 'rxjs';

enum ColorChangeSource {
  User = 'user',
  Programmatic = 'programmatic',
}

@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrl: './color-picker.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true,
    },
  ],
})
export class ColorPickerComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy
{
  // Template refs
  popoverTriggerRef = viewChild<NgpPopoverTrigger>('popoverTriggerRef');
  private pickrHost = viewChild<ElementRef<HTMLElement>>('pickrHost');

  @Input() defaultObjectColor: string | null = null;
  @Input() allowNullColor: boolean = true;

  // * Diameter of the preview swatch in px (24 sits on the form control height; WAP-1687)
  @Input() size: number = 24;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  private pickr?: Pickr;

  // Icons
  faCircleXmark = faCircleXmark;
  faFloppyDisk = faFloppyDisk;
  faTimes = faTimes;
  faXmark = faXmark;

  private _currentColor: string | null = null;

  // ! Indicates that the user is actively typing in the HEX input.
  // ! Used to prevent Pickr change events from echoing back into the HEX field.
  private _isUserTypingHex = false;

  // ! Tracks the source of color changes to DISTINGUISH User interactions from Programmatic updates.
  // ! User: Direct color picker manipulation (dragging, clicking presets, typing hex).
  // ! Programmatic: Internal updates (form writeValue, null mode display color sync).
  private _colorChangeSource: ColorChangeSource = ColorChangeSource.User;

  background = signal<string>('transparent');
  hexInput = signal<string>('');
  isNullMode = signal<boolean>(false);

  // * Preset swatches are grouped into two tabs: the company Chart Color Scheme
  // * ("Custom", shown only when the company has colours) and the built-in defaults.
  customPresets = signal<string[]>([]);
  defaultPresets = signal<string[]>(DEFAULT_PPRESET_COLORS);
  activePresetTab = signal<'custom' | 'default'>('default');

  hasCustomPresets = computed<boolean>(() => this.customPresets().length > 0);

  // * swatches currently rendered (depend on the active tab)
  presets = computed<string[]>(() =>
    this.activePresetTab() === 'custom'
      ? this.customPresets()
      : this.defaultPresets(),
  );

  selectedPreset = signal<string | null>(null);

  // * for split preview mode
  showSplitPreview = signal<boolean>(false);
  leftColor = signal<string>('transparent');
  rightColor = signal<string>('transparent');

  // * Container for the popover
  popoverContainer = signal<HTMLElement | string>('body');

  private store = inject(Store<AppState>);
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef<HTMLElement>);

  private readonly TRANSPARENT_COLOR = 'rgba(0,0,0,0)';
  private readonly NULL_DISPLAY_COLOR = '#FFFFFFDD';

  constructor() {}

  ngOnInit(): void {
    const hasDefaultColor =
      this.defaultObjectColor && this.defaultObjectColor.trim();
    this.showSplitPreview.set(!!hasDefaultColor);

    if (hasDefaultColor) {
      this.leftColor.set(this.defaultObjectColor!);
    }

    // * load the company Chart Color Scheme into the "Custom" tab (WAP-1505).
    // * Invalid values are dropped and duplicates collapsed; when the company has
    // * no scheme the Custom tab is hidden and only the Default presets are shown.
    this.store
      .select(selectUserCompanyColorScheme)
      .pipe(
        tap((companyColors: string[]) => {
          const seen = new Set<string>();
          let customPresets = (companyColors ?? []).filter((color) => {
            if (typeof color !== 'string' || hexToRgb(color) === null) {
              // drop invalid / unparseable colour values
              return false;
            }
            const key = color.trim().toLowerCase();
            if (seen.has(key)) {
              // drop duplicate palette values
              return false;
            }
            seen.add(key);
            return true;
          });

          if (this.defaultObjectColor) {
            // ensure default color is first in the custom presets
            const def = this.defaultObjectColor.toLowerCase();
            customPresets = [
              this.defaultObjectColor,
              ...customPresets.filter((c) => c.toLowerCase() !== def),
            ];
          }

          this.customPresets.set(customPresets);
          // default to the Custom tab when the company has colours, else Default
          this.activePresetTab.set(
            customPresets.length > 0 ? 'custom' : 'default',
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  selectPresetTab(tab: 'custom' | 'default'): void {
    this.activePresetTab.set(tab);
  }

  ngAfterViewInit(): void {
    // Raw NgpPopoverTrigger (not the sui wrapper), so resolve the boundary here.
    this.popoverContainer.set(
      resolveSuiBoundary(this.elementRef.nativeElement),
    );
  }

  ngOnDestroy(): void {
    this.destroyPickr();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: string | null): void {
    // * normalize to string or null
    let stringValue = typeof value === 'string' && value.trim() ? value : null;

    // * if null is not allowed, convert to transparent
    if (stringValue === null && !this.allowNullColor) {
      stringValue = this.TRANSPARENT_COLOR;
    }

    this._currentColor = stringValue;

    // ! handle Null Mode
    if (stringValue === null) {
      this.isNullMode.set(true);
      this.background.set('transparent');
      this.rightColor.set('transparent');
      this.selectedPreset.set(null);
      this.hexInput.set('');

      if (this.pickr) {
        // * prevent change event from exiting null mode
        this._colorChangeSource = ColorChangeSource.Programmatic;

        this.pickr.setColor(this.NULL_DISPLAY_COLOR);
        this.applyNullStyles();

        // * reset flag after change event completes
        setTimeout(() => {
          this._colorChangeSource = ColorChangeSource.User;
        }, 0);
      }
    } else {
      this.isNullMode.set(false);
      this.background.set(stringValue);
      this.rightColor.set(stringValue);
      this.updateSelectedPreset(stringValue);

      if (this.pickr) {
        this.pickr.setColor(stringValue);
        this.removeNullStyles();
      }
    }
  }

  onPopoverOpenChange(isOpen: boolean) {
    if (isOpen) {
      // ! <ng-template> content needs to be rendered before we can init Pickr!!!
      // ! wait for next tick after template is created
      setTimeout(() => {
        this.initPickr();
      }, 0);
    } else {
      // ! and destroy Pickr when popover closes (template content will be destroyed)
      // ! wait for the animation to complete
      setTimeout(() => {
        this.destroyPickr();
      }, 100);
      this.onTouched(); // important for forms
    }
  }

  // ! this is IMPORTANT because the event propagates and triggers other handlers
  // ! - in our case opens PickerDataobjectsDialogComponent
  onPickerKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private initPickr(): void {
    // Clean up any existing instance first
    if (this.pickr) {
      this.destroyPickr();
    }

    if (!this.pickrHost()) {
      return;
    }

    // Use null display color if in null mode, otherwise use current color or transparent fallback
    const initialColor = this.isNullMode()
      ? this.NULL_DISPLAY_COLOR
      : (this._currentColor ?? 'rgba(0,0,0,0)');

    this.pickr = new Pickr({
      el: this.pickrHost()?.nativeElement,
      theme: 'nano',
      inline: true,
      default: initialColor,
      components: {
        preview: true,
        opacity: true,
        hue: true,
      },
      useAsButton: true,
    });

    this.pickr.show();

    // Set initial hex value (unless in null mode)
    if (!this.isNullMode()) {
      this.background.set(this._currentColor);
      this.rightColor.set(this._currentColor);
      this.hexInput.set(this._currentColor);
      this.updateSelectedPreset(this._currentColor);
    } else {
      this.hexInput.set('');
      this.applyNullStyles();
    }

    this.pickr.on('change', (color: Pickr.HSVaColor) => {
      const rgba = color.toRGBA();
      const colorString = `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3]})`;

      // ! Exit Null Mode on user interaction (but not when programmatically setting null color)
      if (
        this.isNullMode() &&
        this._colorChangeSource === ColorChangeSource.User
      ) {
        this.isNullMode.set(false);
        this.removeNullStyles();
      }

      this.background.set(colorString);
      this.rightColor.set(colorString);

      // ! update hex input only if not currently typing and change is from user interaction
      if (
        !this._isUserTypingHex &&
        this._colorChangeSource === ColorChangeSource.User
      ) {
        const hex = rgbaToNormalizedHex(
          rgba as [number, number, number, number],
        );
        this.hexInput.set(hex);
      }

      // Check if transparent (alpha = 0)
      if (rgba[3] === 0) {
        this.selectedPreset.set('transparent');
      } else {
        this.updateSelectedPreset(colorString);
      }
    });
  }

  private destroyPickr(): void {
    if (!this.pickr) return;

    try {
      this.pickr.destroyAndRemove();
    } catch (e) {
      console.log('[ColorPicker] Error destroying Pickr:', e);
    } finally {
      this.pickr = undefined;
    }
  }

  apply() {
    if (!this.pickr) return;

    // Handle null mode
    if (this.isNullMode()) {
      if (this.allowNullColor) {
        this._currentColor = null;
        this.onChange(null);
      } else {
        // * Treat as transparent when null not allowed
        this._currentColor = this.TRANSPARENT_COLOR;
        this.onChange(this.TRANSPARENT_COLOR);
      }
      this.onTouched();
      this.popoverTriggerRef()?.hide();
      return;
    }

    // Normal color mode
    const color = this.pickr.getColor();
    const hex = color.toHEXA().toString();

    this._currentColor = hex;
    this.onChange(hex);
    this.onTouched();

    this.popoverTriggerRef()?.hide();
  }

  cancel() {
    // * Revert to original value (null or color)

    if (this._currentColor === null) {
      // Revert to null state
      this.isNullMode.set(true);
      this.background.set('transparent');
      this.rightColor.set('transparent');
      this.selectedPreset.set(null);
      this.hexInput.set('');
    } else {
      // Revert to original color
      this.isNullMode.set(false);
      this.background.set(this._currentColor);
      this.rightColor.set(this._currentColor);
      this.selectedPreset.set(null);
      this.hexInput.set(''); // Will be recalculated on next open
    }

    this.popoverTriggerRef()?.hide();
  }

  onPresetClick(preset: string) {
    // exit Null Mode
    this.isNullMode.set(false);
    this.removeNullStyles();

    if (this.pickr) {
      this.pickr.setColor(preset);
    }

    this.background.set(preset);
    this.rightColor.set(preset);
    this.selectedPreset.set(preset);
  }

  onTransparentPresetClick() {
    // exit Null Mode
    this.isNullMode.set(false);
    this.removeNullStyles();

    if (this.pickr) {
      this.pickr.setColor(this.TRANSPARENT_COLOR);
      this.background.set('transparent');
      this.rightColor.set('transparent');
      this.selectedPreset.set('transparent');
    }
  }

  onRemoveColorClick() {
    this.isNullMode.set(true);
    this.background.set('transparent');
    this.rightColor.set('transparent');
    this.selectedPreset.set(null);
    this.hexInput.set('');

    if (this.pickr) {
      // Set flag to prevent change event from exiting null mode
      this._colorChangeSource = ColorChangeSource.Programmatic;

      this.pickr.setColor(this.NULL_DISPLAY_COLOR);
      this.applyNullStyles();

      // * set Color Preview to transparent for Null Mode
      const root = this.pickr.getRoot() as any;
      if (root?.preview?.currentColor) {
        root.preview.currentColor.style.setProperty(
          '--pcr-color',
          'transparent',
        );
      }

      // Reset flag after change event completes
      setTimeout(() => {
        this._colorChangeSource = ColorChangeSource.User;
      }, 0);
    }
  }

  isTransparentSelected(): boolean {
    if (!this.pickr || this.isNullMode()) return false;

    const rgba = this.pickr.getColor().toRGBA();
    return rgba[3] === 0;
  }

  onHexInputChange(event: Event): void {
    const input = (event.target as HTMLInputElement).value.trim();

    // Validate hex format: #RRGGBB or #RRGGBBAA (6 or 8 hex digits)
    const hexPattern = /^#?([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

    if (!hexPattern.test(input)) {
      console.warn('[ColorPicker] Invalid hex format:', input);
      return;
    }

    const hexColor = input.startsWith('#') ? input : `#${input}`;

    // exit Null Mode
    this.isNullMode.set(false);
    this.removeNullStyles();

    // ! set FLAG to prevent the change event from updating hex input
    this._isUserTypingHex = true;

    if (this.pickr) {
      this.pickr.setColor(hexColor);
    }

    setTimeout(() => {
      this._isUserTypingHex = false;
    });
  }

  // * check if color matches any preset and select it
  private updateSelectedPreset(color: string | null): void {
    if (!color) {
      this.selectedPreset.set(null);
      return;
    }

    const normalizedColor = color.toLowerCase().trim();
    const matchingPreset = this.presets().find(
      (preset) => preset.toLowerCase().trim() === normalizedColor,
    );

    this.selectedPreset.set(matchingPreset ?? null);
  }

  private applyNullStyles(): void {
    const pickrApp = document.querySelector('.pcr-app[data-theme="nano"]');
    if (pickrApp) {
      pickrApp.classList.add('is-null');
    }
  }

  private removeNullStyles(): void {
    const pickrApp = document.querySelector('.pcr-app[data-theme="nano"]');
    if (pickrApp) {
      pickrApp.classList.remove('is-null');
    }
  }
}
